"use client";

import * as React from "react";
import Link from "next/link";
import { useQuery } from "@tanstack/react-query";
import { useTranslations, useLocale } from "next-intl";
import {
  FileText,
  FolderKanban,
  Clock,
  CheckCircle2,
  ArrowRight,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { spacesApi } from "@/lib/api/spaces";
import { categoriesApi } from "@/lib/api/categories";
import { useAuthStore } from "@/stores/auth-store";
import { SPACE_STATUS_LABELS, type SpaceStatus } from "@/types/space";

const SPACE_STATUS_COLORS: Record<SpaceStatus, string> = {
  draft: "badge-soft-warning",
  active: "badge-soft-success",
  archived: "badge-soft-secondary",
};

export default function DashboardPage() {
  const t = useTranslations("dashboard_page");
  const locale = useLocale();
  const { user, accessToken, _hasHydrated } = useAuthStore();
  const isAuthenticated = _hasHydrated && !!accessToken;

  const { data: spacesData, isLoading: spacesLoading } = useQuery({
    queryKey: ["spaces", "dashboard"],
    queryFn: () => spacesApi.list({ pageSize: 100 }),
    enabled: isAuthenticated,
  });

  const { data: categoriesData, isLoading: categoriesLoading } = useQuery({
    queryKey: ["categories", "dashboard"],
    queryFn: () => categoriesApi.getAll(),
    enabled: isAuthenticated,
  });

  const isLoading = spacesLoading || categoriesLoading;

  // Calculate stats
  const totalSpaces = spacesData?.total || 0;
  const totalCategories = categoriesData?.total || 0;

  const spacesByStatus = React.useMemo(() => {
    if (!spacesData?.items) return {};
    return spacesData.items.reduce((acc, space) => {
      acc[space.status] = (acc[space.status] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
  }, [spacesData]);

  const recentSpaces = spacesData?.items?.slice(0, 5) || [];
  const archivedCount = spacesByStatus["archived"] || 0;

  const StatCard = ({
    title,
    value,
    icon: Icon,
    color = "blue",
    index = 0,
  }: {
    title: string;
    value: number;
    icon: React.ComponentType<{ className?: string }>;
    color?: "blue" | "green" | "purple" | "orange";
    index?: number;
  }) => {
    const colorClasses = {
      blue: { bg: "rgba(30, 183, 255, 0.1)", text: "var(--blue)" },
      green: { bg: "rgba(27, 185, 52, 0.1)", text: "var(--green)" },
      purple: { bg: "rgba(202, 142, 255, 0.1)", text: "var(--purple)" },
      orange: { bg: "rgba(242, 114, 18, 0.1)", text: "var(--orange)" },
    };

    return (
      <Card
        className="card-airframe card-airframe--side-border animate-fade-in-up"
        style={{
          borderLeftColor: colorClasses[color].text,
          animationDelay: `${index * 50}ms`,
        }}
      >
        <CardContent className="p-5">
          <div className="flex items-center gap-4">
            <div
              className="flex h-12 w-12 items-center justify-center rounded-lg"
              style={{ backgroundColor: colorClasses[color].bg }}
            >
              <div style={{ color: colorClasses[color].text }}>
                <Icon className="h-6 w-6" />
              </div>
            </div>
            <div className="flex-1">
              {isLoading ? (
                <>
                  <Skeleton className="h-8 w-16 mb-1" />
                  <Skeleton className="h-4 w-24" />
                </>
              ) : (
                <>
                  <div className="text-2xl font-bold" style={{ color: "var(--gray-900)" }}>
                    {value}
                  </div>
                  <p className="text-sm" style={{ color: "var(--gray-500)" }}>
                    {title}
                  </p>
                </>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="animate-fade-in-up">
        <h1 className="text-3xl font-bold tracking-tight" style={{ color: "var(--gray-900)" }}>
          {t("title")}
        </h1>
        <p style={{ color: "var(--gray-500)" }}>
          {t("welcome", { name: user?.firstname || user?.email || "" })}
        </p>
      </div>

      {/* Stats Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        <StatCard
          title={t("stat_total_spaces")}
          value={totalSpaces}
          icon={FileText}
          color="blue"
          index={0}
        />
        <StatCard
          title={t("stat_categories")}
          value={totalCategories}
          icon={FolderKanban}
          color="orange"
          index={1}
        />
        <StatCard
          title={t("stat_archived")}
          value={archivedCount}
          icon={CheckCircle2}
          color="green"
          index={2}
        />
      </div>

      {/* Status Overview */}
      <div className="grid gap-4 md:grid-cols-2">
        <Card className="card-airframe animate-fade-in-up" style={{ animationDelay: "200ms" }}>
          <CardHeader className="card-header-airframe">
            <CardTitle className="text-base font-semibold" style={{ color: "var(--gray-800)" }}>
              {t("spaces_status_title")}
            </CardTitle>
            <CardDescription style={{ color: "var(--gray-500)" }}>
              {t("spaces_status_description")}
            </CardDescription>
          </CardHeader>
          <CardContent className="card-body-airframe">
            {isLoading ? (
              <div className="space-y-2">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {Object.entries(spacesByStatus).map(([status, count], index) => (
                  <div
                    key={status}
                    className="flex items-center justify-between p-3 rounded-lg transition-colors hover:bg-gray-50 animate-fade-in-up"
                    style={{ animationDelay: `${250 + index * 50}ms` }}
                  >
                    <Badge className={`badge-airframe ${SPACE_STATUS_COLORS[status as SpaceStatus] || ""}`}>
                      {SPACE_STATUS_LABELS[status as SpaceStatus] || status}
                    </Badge>
                    <span className="text-lg font-semibold" style={{ color: "var(--gray-900)" }}>
                      {count}
                    </span>
                  </div>
                ))}
                {Object.keys(spacesByStatus).length === 0 && (
                  <div className="flex flex-col items-center py-8">
                    <FileText className="h-12 w-12 mb-3" style={{ color: "var(--gray-300)" }} />
                    <p className="text-sm" style={{ color: "var(--gray-500)" }}>
                      {t("spaces_empty")}
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="card-airframe animate-fade-in-up" style={{ animationDelay: "250ms" }}>
          <CardHeader className="card-header-airframe flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-base font-semibold" style={{ color: "var(--gray-800)" }}>
                {t("recent_title")}
              </CardTitle>
              <CardDescription style={{ color: "var(--gray-500)" }}>
                {t("recent_description")}
              </CardDescription>
            </div>
            <Link
              href="/spaces"
              className="text-sm flex items-center gap-1 hover:underline"
              style={{ color: "var(--blue)" }}
            >
              {t("view_all")}
              <ArrowRight className="h-4 w-4" />
            </Link>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-5 space-y-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-3">
                    <Skeleton className="h-10 w-10 rounded-full" />
                    <div className="flex-1 space-y-1">
                      <Skeleton className="h-4 w-3/4" />
                      <Skeleton className="h-3 w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div>
                {recentSpaces.map((space, index) => (
                  <Link
                    key={space.id}
                    href={`/spaces/${space.id}`}
                    className="media px-5 py-3 border-b last:border-b-0 hover:bg-gray-50 transition-colors animate-fade-in-up"
                    style={{
                      borderColor: "var(--gray-200)",
                      animationDelay: `${300 + index * 50}ms`,
                    }}
                  >
                    <div className="media-left">
                      <div
                        className="flex h-10 w-10 items-center justify-center rounded-full"
                        style={{ backgroundColor: "rgba(30, 183, 255, 0.1)" }}
                      >
                        <FileText className="h-5 w-5" style={{ color: "var(--blue)" }} />
                      </div>
                    </div>
                    <div className="media-body">
                      <p className="text-sm font-medium truncate" style={{ color: "var(--gray-900)" }}>
                        {space.name}
                      </p>
                      <p className="text-xs" style={{ color: "var(--gray-500)" }}>
                        {new Date(space.createdAt).toLocaleDateString(locale === "en" ? "en-US" : "es-ES")}
                      </p>
                    </div>
                    <div className="media-right">
                      <Badge className={`badge-airframe ${SPACE_STATUS_COLORS[space.status] || ""}`}>
                        {SPACE_STATUS_LABELS[space.status] || space.status}
                      </Badge>
                    </div>
                  </Link>
                ))}
                {recentSpaces.length === 0 && (
                  <div className="flex flex-col items-center py-8">
                    <Clock className="h-12 w-12 mb-3" style={{ color: "var(--gray-300)" }} />
                    <p className="text-sm" style={{ color: "var(--gray-500)" }}>
                      {t("recent_empty")}
                    </p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
